/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentProps } from 'preact';
import type { BundleType } from '#resources/nls/bundle';
import type { ListItemContext as ListItemMenuContext } from '#UNSAFE_Collection';
import type { ListContextMenuConfig } from './List.types';

import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { Menu } from '#UNSAFE_Menu';

type Props<K extends string | number, D> = {
  contextMenuConfig: ListContextMenuConfig<K, D>;
  contextMenuContext: ListItemMenuContext<K, D>;
} & Omit<ComponentProps<typeof Menu>, 'children'>;

/**
 * The internal component used to render a custom context menu on the List
 */
export function ListContextMenu<K extends string | number, D>({
  contextMenuConfig,
  contextMenuContext,
  ...menuProps
}: Props<K, D>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const accessibleLabel =
    contextMenuConfig.accessibleLabel != null
      ? contextMenuConfig.accessibleLabel
      : translations.accContextMenuLabel();

  return contextMenuConfig.itemsRenderer(contextMenuContext) != null ? (
    <Menu aria-label={accessibleLabel} {...menuProps}>
      {menuProps.isOpen && contextMenuConfig.itemsRenderer(contextMenuContext)}
    </Menu>
  ) : (
    <></>
  );
}
